import { useDeferredValue, useEffect, useMemo, useRef } from 'react';

import { LocalizationProvider, TimeField } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { Input, InputProps, mergeSx, SxProps } from '@cast/design-system';

type Props = {
  time: string;
  onTimeChange: (time: string) => void;
  mode?: 'HH:mm' | 'mm';
  label?: string;
  sx?: SxProps;
  InputProps?: InputProps;
};

type TimeProps = Omit<InputProps, 'value'> & {
  value: string;
  mode: Props['mode'];
  onTimeChange: Props['onTimeChange'];
};

const Time = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  error,
  value,
  sx,
  mode,
  onTimeChange,
  ...props
}: TimeProps) => {
  // Requires this hack because pressing backspace types hh/mm, instead, zeroes should appear
  let newValue = value;
  if (mode === 'HH:mm') {
    let [hh, mm] = value.split(':');
    if (hh === 'hh') {
      hh = '00';
    }
    if (mm === 'mm') {
      mm = '00';
    }
    newValue = `${hh}:${mm}`;
  } else {
    if (value === 'mm') {
      newValue = '00';
    }
  }
  const isFirstChange = useRef(true);
  // required to prevent cyclic updates when time is changing rapidly.
  const deferredNewValue = useDeferredValue(newValue);

  useEffect(() => {
    if (isFirstChange.current) {
      isFirstChange.current = false;
      return;
    } else {
      onTimeChange(deferredNewValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferredNewValue]);

  return (
    <Input
      value={newValue}
      readOnly={props.InputProps?.readOnly}
      {...props}
      sx={mergeSx({ width: 64 }, sx)}
    />
  );
};

export const TimeInput = ({
  time,
  onTimeChange,
  mode = 'HH:mm',
  label,
  sx,
  InputProps = {},
}: Props) => {
  const value = useMemo(() => {
    if (mode === 'HH:mm') {
      const [hh, mm] = time.split(':');
      return dayjs()
        .startOf('day')
        .set('hours', Number(hh))
        .set('minutes', Number(mm));
    }
    return dayjs()
      .startOf('day')
      .set('minutes', Number(time) || 0);
  }, [time, mode]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimeField
        format={mode}
        value={value}
        ampm={false}
        slots={{ textField: Time }}
        slotProps={{
          textField: {
            sx,
            onTimeChange,
            mode,
            ...InputProps,
          } as any,
        }}
        minTime={dayjs().startOf('day')}
        maxTime={dayjs().endOf('day')}
        label={label}
      />
    </LocalizationProvider>
  );
};
